import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { integerToCurrency } from '@actual-app/core/shared/util';

import { Link } from '#components/common/Link';
import { cartao, cor, texto } from '#components/finapp/estilo';
import type { MediaDeTipo } from '#components/finapp/motor/medias';
import type { ChaveDeTipo } from '#components/finapp/motor/tiposDeGasto';
import {
  NAO_CLASSIFICADO,
  ROTULO_SEM_TIPO,
} from '#components/finapp/motor/tiposDeGasto';

type Props = {
  /** A média de cada tipo, na janela escolhida. */
  medias: MediaDeTipo[];
  /** O gasto de cada tipo NESTE mês. */
  esteMes: Map<ChaveDeTipo, number>;
  /** Quantos meses entraram na média de verdade. */
  mesesConsiderados: number;
  estreito?: boolean;
  /**
   * ⚠️⚠️ Revisão final da branch (Finding 1, CRITICAL) — vêm de
   * `useHistorico`, via `Inicio.tsx`. Sem eles, uma falha na consulta do
   * histórico (`linhas: []`) fazia este card mostrar TODOS os baldes
   * zerados com "média de 0 meses com movimento" — um número plausível e
   * errado, sem nenhum aviso. Mesma ORDEM de estados que
   * `inicio/ResumoCompromissos.tsx` já usa (ver o JSDoc em `Inicio.tsx`
   * onde `historicoOk` é calculado): erro vence, depois carregando, só
   * então os números.
   */
  carregando?: boolean;
  erro?: Error | null;
};

const ROTULOS: Record<ChaveDeTipo, string> = {
  essencial: 'Essencial',
  luxo: 'Luxo',
  investimento: 'Investimento',
  [NAO_CLASSIFICADO]: ROTULO_SEM_TIPO,
};

const CORES: Record<ChaveDeTipo, string> = {
  essencial: cor.neutro,
  luxo: cor.saida,
  investimento: cor.investimento,
  [NAO_CLASSIFICADO]: cor.tintaFraca,
};

/**
 * Três (ou quatro) números: quanto foi essencial, quanto foi luxo, quanto foi
 * investimento — este mês, contra a média.
 *
 * ⚠️ O balde "Sem tipo" só aparece quando tem valor. Aqui ele é ruído para
 * quem já classificou tudo; o lugar onde ele aparece SEMPRE, mesmo zerado, é a
 * tela Médias (`medias/BlocoDeTipo.tsx`), que é onde se resolve o problema.
 *
 * ⚠️ Divergência do brief: `Link` vem de `#components/common/Link` — ver o
 * JSDoc de `ResumoCompromissos.tsx` para o porquê.
 */
export function GastoMedioPorTipo({
  medias,
  esteMes,
  mesesConsiderados,
  estreito = false,
  carregando = false,
  erro = null,
}: Props) {
  const { t } = useTranslation();

  const visiveis = medias.filter(
    m => m.tipo !== NAO_CLASSIFICADO || (esteMes.get(m.tipo) ?? 0) > 0,
  );

  return (
    <View style={{ ...cartao, gap: 12 }} data-testid="gasto-medio-por-tipo">
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'baseline',
        }}
      >
        <Text style={texto.rotuloCartao}>{t('Como você gasta')}</Text>
        <Link variant="internal" to="/medias" style={texto.rotuloTabela}>
          {t('ver detalhe')}
        </Link>
      </View>

      {erro ? (
        <Text
          data-testid="gasto-medio-por-tipo-erro"
          style={{ ...texto.corpo, color: cor.saida }}
        >
          {t('Não consegui ler seu histórico: {{erro}}', {
            erro: erro.message,
          })}
        </Text>
      ) : carregando ? (
        <Text data-testid="gasto-medio-por-tipo-carregando" style={texto.corpo}>
          <Trans>Carregando…</Trans>
        </Text>
      ) : (
        <>
          <View
            style={{
              flexDirection: estreito ? 'column' : 'row',
              gap: estreito ? 12 : 28,
            }}
          >
            {visiveis.map(({ tipo, media }) => (
              <View key={tipo} style={{ gap: 2, flexGrow: 1 }}>
                <Text style={{ ...texto.rotuloTabela, color: cor.tintaFraca }}>
                  {t(ROTULOS[tipo])}
                </Text>
                <Text style={{ ...texto.valorMedio, color: CORES[tipo] }}>
                  {integerToCurrency(esteMes.get(tipo) ?? 0)}
                </Text>
                <Text style={{ ...texto.corpo, color: cor.tintaFraca }}>
                  {t('média {{valor}}', { valor: integerToCurrency(media) })}
                </Text>
              </View>
            ))}
          </View>

          <Text style={{ ...texto.corpo, color: cor.tintaFraca }}>
            {mesesConsiderados === 1
              ? t('média de 1 mês com movimento')
              : t('média de {{n}} meses com movimento', {
                  n: mesesConsiderados,
                })}
          </Text>
        </>
      )}
    </View>
  );
}
